/**
 * Gate screen 1/2 — group picker. 1:1 from the golden mock
 * (~/mockups/location-login-flow.html #view-group): location pill, title,
 * subtitle, emoji tile grid, lock note. Presentational only — data comes
 * from the LocationGate container (Task 2.2,
 * docs/plans/2026-07-30-location-login.md).
 *
 * Tiles render the group's `icon_url` (backfilled WebP) when present;
 * groups without one fall back to the fixed TILE_EMOJI rotation.
 *
 * rb3↔rb5 campus (2026-08-26): `groups` already carries BOTH locations'
 * groups when a sibling exists (LocationGate → useLocationGroups). Own
 * building first, sibling tiles badged — no toggle, no localStorage.
 * One campus, both iPads, always.
 */

import { useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { tileTone, TILE_INTERACTIVE } from '@/modules/kiosk/utils/tileTones';
import { TILE_EMOJI } from '../constants';
import { signOutLocation } from '../utils/signOutLocation';
import { SignOutConfirm } from './SignOutConfirm';
import type { LocationSessionGroup } from '../types';

export interface GroupPickerProps {
  locationName: string;
  locationCode: string;
  groups: LocationSessionGroup[];
  /** Uppercased/lowercased is irrelevant here — compared as stored, e.g. 'rb3'. Null = no sibling campus. */
  siblingLocationCode?: string | null;
  onPick: (group: LocationSessionGroup) => void;
}

export function GroupPicker({
  locationName,
  locationCode,
  groups,
  siblingLocationCode = null,
  onPick,
}: GroupPickerProps) {
  const { signOut } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleSignOut(): Promise<void> {
    setIsSigningOut(true);
    try {
      await signOutLocation(signOut, locationCode);
    } catch (err) {
      setIsSigningOut(false);
      // Re-throw so SignOutConfirm keeps the dialog open and surfaces the error.
      throw err instanceof Error ? err : new Error('Sign out failed — try again');
    }
  }

  const ownGroups = groups.filter((g) => g.locationCode === locationCode);
  const siblingGroups = siblingLocationCode
    ? groups.filter((g) => g.locationCode === siblingLocationCode)
    : [];
  const displayedGroups = [...ownGroups, ...siblingGroups];

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-6 py-10">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-extrabold text-primary">
          📍 {locationName} ({locationCode.toUpperCase()})
        </div>
        {siblingLocationCode && (
          <div className="flex h-8 items-center rounded-lg border-2 border-primary bg-primary px-2 text-[9px] font-black leading-none text-primary-foreground">
            +{siblingLocationCode.toUpperCase()}
          </div>
        )}
      </div>
      <h1 className="mb-1 text-2xl font-extrabold text-foreground">Choose a group</h1>
      <p className="mb-8 max-w-xs text-center text-sm text-muted-foreground">
        You're on the {locationCode.toUpperCase()} location account — pick where you're working.
      </p>

      <div className="grid w-full max-w-sm grid-cols-3 gap-4">
        {displayedGroups.map((g, i) => {
          const isSibling = g.locationCode !== locationCode;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onPick(g)}
              className="flex flex-col items-center gap-1.5"
            >
              <span
                className={cn(
                  'relative flex h-16 w-16 items-center justify-center rounded-full border-2 text-2xl',
                  TILE_INTERACTIVE,
                  tileTone(i),
                )}
              >
                {g.iconUrl ? (
                  <img
                    src={g.iconUrl}
                    alt=""
                    loading="lazy"
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  TILE_EMOJI[i % TILE_EMOJI.length]
                )}
                {isSibling && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1 text-[8px] font-black leading-[14px] text-primary-foreground">
                    {g.locationCode.toUpperCase()}
                  </span>
                )}
              </span>
              <span className="text-xs font-bold text-foreground">{g.name}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        🔒 App unlocks after you pick who you are
      </p>

      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={isSigningOut}
        className="mt-4 text-xs font-semibold text-muted-foreground/70 underline-offset-2 hover:text-muted-foreground hover:underline disabled:pointer-events-none disabled:opacity-50"
      >
        Sign out of this device
      </button>

      <SignOutConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleSignOut}
        isSigningOut={isSigningOut}
      />
    </div>
  );
}
